// components/crud/ClienteForm.tsx
import React, { useState, useEffect } from 'react';
import { useCreateWithEmpresa } from '../../hooks/useServicesWithEmpresa';
import { useEmpresaContext } from '../../contexts/EmpresaContext';
import { empresaService } from '../../services/entitiesServices';
import { useAuthSimple } from '../../hooks/useAuth';
import type { Cliente } from '../../types/entities';
import { toSaoPauloTimestamp } from '../../utils/formatters';
import type { Empresa } from '../../types/api';

interface ClienteFormProps {
  cliente?: Cliente; // Se fornecido, estamos editando
  onSuccess?: (cliente: Cliente) => void;
  onCancel?: () => void;
  // Se fornecido, override do submit. Recebe dados preparados (API shape) e deve retornar o cliente salvo.
  overrideSubmit?: (apiData: any) => Promise<any>;
  // initialValues: usado para pré-preencher o formulário sem ativar o modo de edição
  initialValues?: Partial<Cliente>;
}

export const ClienteForm: React.FC<ClienteFormProps> = ({ cliente, onSuccess, onCancel, overrideSubmit, initialValues }) => {
  const { createCliente, updateCliente } = useCreateWithEmpresa();
  const { empresaId: userEmpresaId, userType, isUserFromEmpresa, isUserFromGrupo } = useEmpresaContext();
  const { user } = useAuthSimple();
  const userGrupoId = user?.grupoId;
  
  // Estados para empresas
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  
  const [formData, setFormData] = useState({
    // Campos obrigatórios
    cpfCnpj: '',
    nome: '',
    email: '',
    empresaId: '',
    // Campos opcionais - dados pessoais
    apelido: '',
    emailAlternativo: '',
    telefone: '',
    dataNascimentoAbertura: '',
    tipoPessoa: '',
    tipoCliente: '',
    sexo: '',
    estadoCivil: '',
    naturalidade: '',
    dataInclusao: '',
    
    // Documentos
    tipoDocumento: '',
    numeroRg: '',
    orgaoEmissor: '',
    dataExpedicao: '',
    numeroCnh: '',
    vencimentoCnh: '',
    // Dados bancários/financeiros
    titularContaCartao: '',
    tipoConta: '',
    cpfTitular: '',
    banco: '',
    agencia: '',
    digitoAgencia: '',
    contaCorrente: '',
    digitoConta: '',
    bandeiraCartao: '',
    validadeCartao: '',
    cartaoCredito: '',
    codigoExterno: '',
    // Endereço
    endereco: '',
    bairro: '',
    cep: '',
    cidade: '',
    estado: '',
    // Outros
    codigoSistema: '',
    dataAgendamentoAniversario: '',
    dataPosVenda: '',
    observacoesInternas: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const convertTimestampToDate = (timestamp: string) => {
    if (!timestamp) return '';
    return new Date(timestamp).toISOString().split('T')[0];
  };

  // Preencher form se estamos editando ou se initialValues são fornecidos
  useEffect(() => {
    if (cliente) {
      setFormData({
        // Campos obrigatórios
        cpfCnpj: cliente.cpfCnpj || '',
        nome: cliente.nome || '',
        email: cliente.email || '',
        empresaId: cliente.empresaId || '',
        // Campos opcionais - dados pessoais
        apelido: cliente.apelido || '',
        emailAlternativo: cliente.emailAlternativo || '',
        telefone: cliente.telefone || '',
        dataNascimentoAbertura: convertTimestampToDate(cliente.dataNascimentoAbertura || ''),
        tipoPessoa: cliente.tipoPessoa || '',
        tipoCliente: cliente.tipoCliente || '',
        sexo: cliente.sexo || '',
        estadoCivil: cliente.estadoCivil || '',
        naturalidade: cliente.naturalidade || '',
  dataInclusao: convertTimestampToDate(cliente.dataInclusao || ''),
        // Documentos
        tipoDocumento: cliente.tipoDocumento || '',
        numeroRg: cliente.numeroRg || '',
        orgaoEmissor: cliente.orgaoEmissor || '',
        dataExpedicao: convertTimestampToDate(cliente.dataExpedicao || ''),
        numeroCnh: cliente.numeroCnh || '',
        vencimentoCnh: convertTimestampToDate(cliente.vencimentoCnh || ''),
        // Dados bancários/financeiros
        titularContaCartao: cliente.titularContaCartao || '',
        tipoConta: cliente.tipoConta || '',
        cpfTitular: cliente.cpfTitular || '',
        banco: cliente.banco || '',
        agencia: cliente.agencia || '',
        digitoAgencia: cliente.digitoAgencia || '',
        contaCorrente: cliente.contaCorrente || '',
        digitoConta: cliente.digitoConta || '',
        bandeiraCartao: cliente.bandeiraCartao || '',
        validadeCartao: cliente.validadeCartao || '',
        cartaoCredito: cliente.cartaoCredito || '',
        codigoExterno: cliente.codigoExterno || '',
        // Endereço
        endereco: cliente.endereco || '',
        bairro: cliente.bairro || '',
        cep: cliente.cep || '',
        cidade: cliente.cidade || '',
        estado: cliente.estado || '',
        // Outros
        codigoSistema: cliente.codigoSistema || '',
        dataAgendamentoAniversario: convertTimestampToDate(cliente.dataAgendamentoAniversario || ''),
        dataPosVenda: convertTimestampToDate(cliente.dataPosVenda || ''),
        observacoesInternas: (cliente as any).observacoesInternas || '',
      });
    }
  }, [cliente]);

  // Se initialValues foram passadas e não estamos em modo edição, pré-preenche o form
  useEffect(() => {
    if (!cliente && typeof initialValues !== 'undefined' && initialValues !== null) {
      setFormData(prev => ({
        ...prev,
        cpfCnpj: (initialValues as any).cpfCnpj ?? prev.cpfCnpj,
        nome: (initialValues as any).nome ?? prev.nome,
        email: (initialValues as any).email ?? prev.email,
        empresaId: (initialValues as any).empresaId ?? prev.empresaId,
        apelido: (initialValues as any).apelido ?? prev.apelido,
        telefone: (initialValues as any).telefone ?? prev.telefone,
        codigoExterno: (initialValues as any).codigoExterno ?? prev.codigoExterno,
        dataNascimentoAbertura: (initialValues as any).dataNascimentoAbertura ? convertTimestampToDate((initialValues as any).dataNascimentoAbertura) : prev.dataNascimentoAbertura,
      }));
    }
  }, [initialValues, cliente]);

  // Inicializar empresaId e carregar empresas
  useEffect(() => {
    const initializeEmpresa = async () => {
      // Para usuários de empresa, pré-selecionar a empresa
      if (isUserFromEmpresa && userEmpresaId && !formData.empresaId) {
        setFormData(prev => ({ ...prev, empresaId: userEmpresaId }));
      }

      // Carregar empresas se for usuário de grupo ou master
      if (isUserFromGrupo || userType === 'master') {
        await loadEmpresas();
      }
    };

    initializeEmpresa();
  }, [userEmpresaId, userType, isUserFromEmpresa, isUserFromGrupo]);

  const loadEmpresas = async () => {
    setLoadingEmpresas(true);
    try {
      let listRes: any = null;

      if (isUserFromGrupo && userType === 'grupo' && userGrupoId) {
        // Prefer endpoint que retorna empresas do grupo
        try {
          listRes = await empresaService.listByGrupo(userGrupoId);
        } catch (grpErr) {
          console.warn('Erro ao buscar empresas por grupo, caindo para list():', grpErr);
          listRes = await empresaService.list();
        }
      } else {
        listRes = await empresaService.list();
      }

      // Normalizar formatos de resposta possíveis
      let empresasData: any[] = [];
      if (Array.isArray(listRes)) empresasData = listRes;
      else if (listRes && Array.isArray(listRes.data)) empresasData = listRes.data;
      else if (listRes && Array.isArray(listRes.items)) empresasData = listRes.items;
      else if (listRes && Array.isArray(listRes.empresas)) empresasData = listRes.empresas;
      else empresasData = [];

      setEmpresas(empresasData);
    } catch (err) {
      console.error('Erro ao carregar empresas:', err);
      setEmpresas([]);
    } finally {
      setLoadingEmpresas(false);
    }
  };

  const convertDateToTimestamp = (dateString: string) => {
    return toSaoPauloTimestamp(dateString);
  };

  const prepareDataForAPI = (data: typeof formData) => {
    // Omitimos 'situacao' do payload para create/update (campo removido do form)
    const rest = { ...data };

    return {
      ...rest,
      // Converter datas para timestamp ISO
      dataNascimentoAbertura: convertDateToTimestamp(data.dataNascimentoAbertura),
      dataInclusao: convertDateToTimestamp(data.dataInclusao),
      dataExpedicao: convertDateToTimestamp(data.dataExpedicao),
      vencimentoCnh: convertDateToTimestamp(data.vencimentoCnh),
      dataAgendamentoAniversario: convertDateToTimestamp(data.dataAgendamentoAniversario),
      dataPosVenda: convertDateToTimestamp(data.dataPosVenda),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Preparar dados com timestamps
      const apiData = prepareDataForAPI(formData);
      
      let result;

      if (typeof overrideSubmit === 'function') {
        // Permite ao pai controlar onde o submit vai (ex: /cliente ao invés do padrão)
        const saved = await overrideSubmit(apiData);
        // Se o override retornar objeto com data use-o, senão assume que retornou o cliente
        const clienteSaved = saved?.data ?? saved;
        onSuccess?.(clienteSaved);
      } else {
        if (cliente) {
          // Editando cliente existente - usa hook que adiciona empresaId automaticamente
          result = await updateCliente(cliente.id, apiData);
        } else {
          // Criando novo cliente - usa hook que adiciona empresaId automaticamente
          result = await createCliente(apiData);
        }

        onSuccess?.(result.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const isEdit = !!cliente;

  return (
    <div className="p-6 rounded-lg" style={{ backgroundColor: '#FFFFFF', border: '1px solid #C3B4DE' }}>
      <div className="mb-6">
        <h3 className="text-xs font-semibold" style={{ color: '#3d2b56' }}>
          {isEdit ? 'Editar Cliente' : 'Novo Cliente'}
        </h3>
        <p className="text-xs mt-1" style={{ color: '#9E9E9E' }}>
          {isEdit ? 'Altere os dados do cliente abaixo.' : 'Preencha os dados do novo cliente.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Dados Básicos */}
        <div className="border rounded-lg p-4" style={{ borderColor: '#C3B4DE' }}>
          <h3 className="text-xs font-medium mb-1" style={{ color: '#5d54a4' }}>Dados Básicos</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {/* CPF/CNPJ - Campo obrigatório */}
            <div>
              <label htmlFor="cpfCnpj" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                CPF/CNPJ *
              </label>
              <input
                id="cpfCnpj"
                name="cpfCnpj"
                type="text"
                required
                value={formData.cpfCnpj}
                onChange={handleChange('cpfCnpj')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: 123.456.789-00"
              />
            </div>

            {/* Nome - Campo obrigatório */}
            <div>
              <label htmlFor="nome" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Nome Completo *
              </label>
              <input
                id="nome"
                name="nome"
                type="text"
                required
                value={formData.nome}
                onChange={handleChange('nome')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: João Silva Santos"
              />
            </div>

            {/* Apelido */}
            <div>
              <label htmlFor="apelido" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Apelido
              </label>
              <input
                id="apelido"
                name="apelido"
                type="text"
                value={formData.apelido}
                onChange={handleChange('apelido')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: João"
              />
            </div>

            {/* Email - Campo obrigatório */}
            <div>
              <label htmlFor="email" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Email *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange('email')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: joao@email.com"
              />
            </div>

            {/* Empresa - Campo obrigatório */}
            <div>
              <label htmlFor="empresaId" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Empresa *
              </label>
              <select
                id="empresaId"
                name="empresaId"
                required
                value={formData.empresaId}
                onChange={handleChange('empresaId')}
                disabled={isUserFromEmpresa || loadingEmpresas}
                className={`block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs ${
                  isUserFromEmpresa ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: isUserFromEmpresa ? '#F5F5F5' : '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => !isUserFromEmpresa && (e.target.style.borderColor = '#7A61B2')}
              >
                <option value="">
                  {loadingEmpresas 
                    ? 'Carregando empresas...' 
                    : 'Selecione uma empresa'
                  }
                </option>
                {!loadingEmpresas && empresas.map((empresa) => (
                  <option key={empresa.id} value={empresa.id}>
                    {empresa.nome}
                  </option>
                ))}
              </select>
              {isUserFromEmpresa && (
                <p className="text-xs text-gray-500 mt-1">
                  Empresa pré-selecionada baseada no seu usuário
                </p>
              )}
              {loadingEmpresas && (
                <p className="text-xs text-gray-500 mt-1">
                  Carregando empresas disponíveis...
                </p>
              )}
            </div>

            {/* Email Alternativo */}
            <div>
              <label htmlFor="emailAlternativo" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Email Alternativo
              </label>
              <input
                id="emailAlternativo"
                name="emailAlternativo"
                type="email"
                value={formData.emailAlternativo}
                onChange={handleChange('emailAlternativo')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: joao.alt@email.com"
              />
            </div>

            {/* Telefone */}
            <div>
              <label htmlFor="telefone" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Telefone
              </label>
              <input
                id="telefone"
                name="telefone"
                type="tel"
                value={formData.telefone}
                onChange={handleChange('telefone')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: (11) 99999-9999"
              />
            </div>
          </div>
        </div>

        {/* Dados Pessoais */}
        <div className="border rounded-lg p-4" style={{ borderColor: '#C3B4DE' }}>
          <h3 className="text-xs font-medium mb-1" style={{ color: '#5d54a4' }}>Dados Pessoais</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {/* Data Nascimento/Abertura */}
            <div>
              <label htmlFor="dataNascimentoAbertura" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Data Nascimento/Abertura
              </label>
              <input
                id="dataNascimentoAbertura"
                name="dataNascimentoAbertura"
                type="date"
                value={formData.dataNascimentoAbertura}
                onChange={handleChange('dataNascimentoAbertura')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
              />
            </div>

            {/* Tipo Pessoa */}
            <div>
              <label htmlFor="tipoPessoa" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Tipo Pessoa
              </label>
              <select
                id="tipoPessoa"
                name="tipoPessoa"
                value={formData.tipoPessoa}
                onChange={handleChange('tipoPessoa')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
              >
                <option value="">Selecione</option>
                <option value="fisica">Pessoa Física</option>
                <option value="juridica">Pessoa Jurídica</option>
              </select>
            </div>

            {/* Tipo Cliente */}
            <div>
              <label htmlFor="tipoCliente" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Tipo Cliente
              </label>
              <select
                id="tipoCliente"
                name="tipoCliente"
                value={formData.tipoCliente}
                onChange={handleChange('tipoCliente')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
              >
                <option value="">Selecione</option>
                <option value="prospecto">Prospecto</option>
                <option value="cliente">Cliente</option>
                <option value="ex-cliente">Ex-Cliente</option>
              </select>
            </div>

            {/* Sexo */}
            <div>
              <label htmlFor="sexo" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Sexo
              </label>
              <select
                id="sexo"
                name="sexo"
                value={formData.sexo}
                onChange={handleChange('sexo')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
              >
                <option value="">Selecione</option>
                <option value="masculino">Masculino</option>
                <option value="feminino">Feminino</option>
                <option value="outro">Outro</option>
              </select>
            </div>

            {/* Estado Civil */}
            <div>
              <label htmlFor="estadoCivil" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Estado Civil
              </label>
              <select
                id="estadoCivil"
                name="estadoCivil"
                value={formData.estadoCivil}
                onChange={handleChange('estadoCivil')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
              >
                <option value="">Selecione</option>
                <option value="solteiro">Solteiro(a)</option>
                <option value="casado">Casado(a)</option>
                <option value="divorciado">Divorciado(a)</option>
                <option value="viuvo">Viúvo(a)</option>
                <option value="uniao_estavel">União Estável</option>
              </select>
            </div>

            {/* Naturalidade */}
            <div>
              <label htmlFor="naturalidade" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Naturalidade
              </label>
              <input
                id="naturalidade"
                name="naturalidade"
                type="text"
                value={formData.naturalidade}
                onChange={handleChange('naturalidade')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: São Paulo/SP"
              />
            </div>
          </div>
        </div>

        {/* Endereço */}
        <div className="border rounded-lg p-4" style={{ borderColor: '#C3B4DE' }}>
          <h3 className="text-xs font-medium mb-1" style={{ color: '#5d54a4' }}>Endereço</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {/* CEP */}
            <div>
              <label htmlFor="cep" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                CEP
              </label>
              <input
                id="cep"
                name="cep"
                type="text"
                value={formData.cep}
                onChange={handleChange('cep')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: 01234-567"
              />
            </div>

            {/* Endereço */}
            <div className="md:col-span-2">
              <label htmlFor="endereco" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Endereço
              </label>
              <input
                id="endereco"
                name="endereco"
                type="text"
                value={formData.endereco}
                onChange={handleChange('endereco')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: Rua das Flores, 123"
              />
            </div>

            {/* Bairro */}
            <div>
              <label htmlFor="bairro" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Bairro
              </label>
              <input
                id="bairro"
                name="bairro"
                type="text"
                value={formData.bairro}
                onChange={handleChange('bairro')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: Centro"
              />
            </div>

            {/* Cidade */}
            <div>
              <label htmlFor="cidade" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Cidade
              </label>
              <input
                id="cidade"
                name="cidade"
                type="text"
                value={formData.cidade}
                onChange={handleChange('cidade')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: São Paulo"
              />
            </div>

            {/* Estado */}
            <div>
              <label htmlFor="estado" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Estado
              </label>
              <select
                id="estado"
                name="estado"
                value={formData.estado}
                onChange={handleChange('estado')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
              >
                <option value="">Selecione</option>
                <option value="AC">Acre</option>
                <option value="AL">Alagoas</option>
                <option value="AP">Amapá</option>
                <option value="AM">Amazonas</option>
                <option value="BA">Bahia</option>
                <option value="CE">Ceará</option>
                <option value="DF">Distrito Federal</option>
                <option value="ES">Espírito Santo</option>
                <option value="GO">Goiás</option>
                <option value="MA">Maranhão</option>
                <option value="MT">Mato Grosso</option>
                <option value="MS">Mato Grosso do Sul</option>
                <option value="MG">Minas Gerais</option>
                <option value="PA">Pará</option>
                <option value="PB">Paraíba</option>
                <option value="PR">Paraná</option>
                <option value="PE">Pernambuco</option>
                <option value="PI">Piauí</option>
                <option value="RJ">Rio de Janeiro</option>
                <option value="RN">Rio Grande do Norte</option>
                <option value="RS">Rio Grande do Sul</option>
                <option value="RO">Rondônia</option>
                <option value="RR">Roraima</option>
                <option value="SC">Santa Catarina</option>
                <option value="SP">São Paulo</option>
                <option value="SE">Sergipe</option>
                <option value="TO">Tocantins</option>
              </select>
            </div>
          </div>
        </div>

        {/* Documentos */}
        <div className="border rounded-lg p-4" style={{ borderColor: '#C3B4DE' }}>
          <h3 className="text-xs font-medium mb-1" style={{ color: '#5d54a4' }}>Documentos</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {/* Tipo Documento */}
            <div>
              <label htmlFor="tipoDocumento" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Tipo Documento
              </label>
              <select
                id="tipoDocumento"
                name="tipoDocumento"
                value={formData.tipoDocumento}
                onChange={handleChange('tipoDocumento')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
              >
                <option value="">Selecione</option>
                <option value="rg">RG</option>
                <option value="cnh">CNH</option>
                <option value="passaporte">Passaporte</option>
              </select>
            </div>

            {/* Número RG */}
            <div>
              <label htmlFor="numeroRg" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Número RG
              </label>
              <input
                id="numeroRg"
                name="numeroRg"
                type="text"
                value={formData.numeroRg}
                onChange={handleChange('numeroRg')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: 12.345.678-9"
              />
            </div>

            {/* Órgão Emissor */}
            <div>
              <label htmlFor="orgaoEmissor" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Órgão Emissor
              </label>
              <input
                id="orgaoEmissor"
                name="orgaoEmissor"
                type="text"
                value={formData.orgaoEmissor}
                onChange={handleChange('orgaoEmissor')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: SSP/SP"
              />
            </div>

            {/* Data Expedição */}
            <div>
              <label htmlFor="dataExpedicao" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Data Expedição
              </label>
              <input
                id="dataExpedicao"
                name="dataExpedicao"
                type="date"
                value={formData.dataExpedicao}
                onChange={handleChange('dataExpedicao')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
              />
            </div>

            {/* Número CNH */}
            <div>
              <label htmlFor="numeroCnh" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Número CNH
              </label>
              <input
                id="numeroCnh"
                name="numeroCnh"
                type="text"
                value={formData.numeroCnh}
                onChange={handleChange('numeroCnh')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: 12345678901"
              />
            </div>

            {/* Vencimento CNH */}
            <div>
              <label htmlFor="vencimentoCnh" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Vencimento CNH
              </label>
              <input
                id="vencimentoCnh"
                name="vencimentoCnh"
                type="date"
                value={formData.vencimentoCnh}
                onChange={handleChange('vencimentoCnh')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
              />
            </div>
          </div>
        </div>

        {/* Dados Bancários */}
        <div className="border rounded-lg p-4" style={{ borderColor: '#C3B4DE' }}>
          <h3 className="text-xs font-medium mb-1" style={{ color: '#5d54a4' }}>Dados Bancários</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {/* Banco */}
            <div>
              <label htmlFor="banco" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Banco
              </label>
              <input
                id="banco"
                name="banco"
                type="text"
                value={formData.banco}
                onChange={handleChange('banco')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: Banco do Brasil"
              />
            </div>

            {/* Agência */}
            <div>
              <label htmlFor="agencia" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Agência
              </label>
              <input
                id="agencia"
                name="agencia"
                type="text"
                value={formData.agencia}
                onChange={handleChange('agencia')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: 1234"
              />
            </div>

            {/* Dígito Agência */}
            <div>
              <label htmlFor="digitoAgencia" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Dígito Agência
              </label>
              <input
                id="digitoAgencia"
                name="digitoAgencia"
                type="text"
                value={formData.digitoAgencia}
                onChange={handleChange('digitoAgencia')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: 5"
              />
            </div>

            {/* Conta Corrente */}
            <div>
              <label htmlFor="contaCorrente" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Conta Corrente
              </label>
              <input
                id="contaCorrente"
                name="contaCorrente"
                type="text"
                value={formData.contaCorrente}
                onChange={handleChange('contaCorrente')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: 12345678"
              />
            </div>

            {/* Dígito Conta */}
            <div>
              <label htmlFor="digitoConta" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Dígito Conta
              </label>
              <input
                id="digitoConta"
                name="digitoConta"
                type="text"
                value={formData.digitoConta}
                onChange={handleChange('digitoConta')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
                placeholder="Ex: 9"
              />
            </div>

            {/* Tipo Conta */}
            <div>
              <label htmlFor="tipoConta" className="block text-xs font-medium mb-1" style={{ color: '#121212' }}>
                Tipo Conta
              </label>
              <select
                id="tipoConta"
                name="tipoConta"
                value={formData.tipoConta}
                onChange={handleChange('tipoConta')}
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
              >
                <option value="">Selecione</option>
                <option value="corrente">Conta Corrente</option>
                <option value="poupanca">Conta Poupança</option>
                <option value="salario">Conta Salário</option>
              </select>
            </div>
          </div>
        </div>

        {/* Mensagem de erro */}
        {error && (
          <div className="p-3 rounded-md bg-red-50">
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}

        {/* Observações Internas */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Observações Internas</label>
          <textarea
            value={(formData as any).observacoesInternas || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, observacoesInternas: e.target.value }))}
            rows={4}
            placeholder="Anotações internas sobre o cliente (não visível ao cliente)"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
          />
        </div>

        {/* Botões */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="px-2 py-1 text-xs font-medium text-white rounded-md hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            style={{ backgroundColor: '#7A61B2' }}
          >
            {loading ? 'Salvando...' : isEdit ? 'Atualizar' : 'Criar Cliente'}
          </button>
          
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-2 py-1 text-xs font-medium rounded-md border transition-colors"
              style={{ 
                borderColor: '#C3B4DE', 
                color: '#5d54a4',
                backgroundColor: 'transparent'
              }}
            >
              Cancelar
            </button>
          )}
        </div>
      </form>
    </div>
  );
};
